import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import type { DestinationContext } from "@/lib/dashboard-home/destinations";
import {
  MAX_SHORTCUTS,
  type StoredShortcut,
} from "@/lib/dashboard-home/shortcuts";
import { ArrowLeft, FileText, Pin, Zap } from "lucide-react";
import { ShortcutCounter } from "../ShortcutCounter";
import { ActionsTab } from "./ActionsTab";
import { PageTab } from "./PageTab";
import { RecordTab } from "./RecordTab";

const TAB_TRIGGER_CLASS = "min-w-0 flex-1 gap-2 whitespace-normal py-2";

/** Second écran de la modale des raccourcis : choisir ce que l'on ajoute. */
export const AddShortcutView = ({
  shortcuts,
  context,
  onAdd,
  onBack,
  onOpenPath,
}: {
  shortcuts: StoredShortcut[];
  context: DestinationContext;
  onAdd: (shortcut: StoredShortcut) => void;
  onBack: () => void;
  /** Quitte l'accueil pour aller choisir une fiche à épingler. */
  onOpenPath: (path: string) => void;
}) => {
  const vt = useVisibleT();
  const full = shortcuts.length >= MAX_SHORTCUTS;

  return (
    <>
      <header className="flex items-center gap-3 border-b border-slate-200 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="flex size-10 shrink-0 items-center justify-center rounded-md bg-slate-100 transition-colors hover:bg-slate-200"
          aria-label={vt("bo.home.shortcuts.back", "Retour aux raccourcis")}
        >
          <ArrowLeft className="size-5" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <DialogTitle className="text-lg font-semibold">
            {vt("bo.home.shortcuts.addTitle", "Ajouter un raccourci")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {vt(
              "bo.home.shortcuts.addSubtitle",
              "Choisissez une action, une fiche ou une page. Le raccourci est enregistré aussitôt.",
            )}
          </DialogDescription>
        </div>
        <ShortcutCounter count={shortcuts.length} />
      </header>

      {full && (
        <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {vt(
            "bo.home.shortcuts.fullHint",
            "Vous avez atteint le maximum de raccourcis : supprimez-en un pour en ajouter un autre.",
          )}
        </p>
      )}

      <Tabs defaultValue="actions" className="mt-4 flex flex-col gap-4">
        <TabsList className="h-auto items-stretch border-b border-slate-200">
          <TabsTrigger value="actions" className={TAB_TRIGGER_CLASS}>
            <Zap className="size-4 shrink-0" aria-hidden />
            {vt("bo.home.shortcuts.tabActions", "Une action")}
          </TabsTrigger>
          <TabsTrigger value="record" className={TAB_TRIGGER_CLASS}>
            <Pin className="size-4 shrink-0" aria-hidden />
            {vt("bo.home.shortcuts.tabRecord", "Une fiche")}
          </TabsTrigger>
          <TabsTrigger value="page" className={TAB_TRIGGER_CLASS}>
            <FileText className="size-4 shrink-0" aria-hidden />
            {vt("bo.home.shortcuts.tabPage", "Une page")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="actions" className="mt-0">
          <ActionsTab
            shortcuts={shortcuts}
            context={context}
            full={full}
            onAdd={onAdd}
          />
        </TabsContent>
        <TabsContent value="record" className="mt-0">
          <RecordTab
            shortcuts={shortcuts}
            context={context}
            onOpenPath={onOpenPath}
          />
        </TabsContent>
        <TabsContent value="page" className="mt-0">
          <PageTab
            shortcuts={shortcuts}
            context={context}
            full={full}
            onAdd={onAdd}
          />
        </TabsContent>
      </Tabs>
    </>
  );
};
